import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Security,
  Fingerprint,
  Timeline,
  Storage,
  Warning,
  Speed,
  Lock
} from '@mui/icons-material';
// import Mermaid from 'react-mermaid2';
import MermaidDiagram from '../components/objects/MermaidDiagram';

const BlockchainIntegration = () => {
  const navigate = useNavigate();
  const [selectedDiagram, setSelectedDiagram] = useState('radicacion');

  const conceptosClave = [
    {
      titulo: 'Integridad',
      descripcion: 'El documento no ha sido alterado desde su radicación',
      icon: <CheckCircle color="success" />
    },
    {
      titulo: 'Trazabilidad Auditiva',
      descripcion: 'Historial indeleble de quién, cuándo y qué acción se realizó',
      icon: <Timeline color="primary" />
    },
    {
      titulo: 'No Repudio',
      descripcion: 'El autor no puede negar haber radicado el documento',
      icon: <Fingerprint color="secondary" />
    },
    {
      titulo: 'Transparencia Controlada',
      descripción: 'Control de acceso al historial según tipo de blockchain',
      icon: <Security color="info" />
    }
  ];

  const arquitectura = [
    {
      titulo: 'Almacenamiento Seguro',
      descripcion: 'El documento secreto se almacena en repositorio seguro (BD/Blob Storage encriptado)',
      tipo: 'Off-Chain',
      color: 'primary'
    },
    {
      titulo: 'Blockchain como Sello',
      descripcion: 'Solo el Hash criptogrÁfico SHA-256 + metadatos se envía a la Blockchain',
      tipo: 'On-Chain',
      color: 'secondary'
    }
  ];

  const consideracionesSeguridad = [
    {
      categoria: 'Claves Privadas',
      punto: 'NUNCA están en código. Se usa Azure Key Vault o AWS Secrets Manager',
      severidad: 'critical'
    },
    {
      categoria: 'Tipo de Blockchain',
      punto: 'Blockchain Privada/Permisionada (Hyperledger Fabric) es la recomendada para documentos secretos',
      severidad: 'high'
    },
    {
      categoria: 'Costos y Rendimiento',
      punto: 'En Ethereum, cada transacción tiene costo (gas). Redes privadas eliminan este problema',
      severidad: 'medium'
    },
    {
      categoria: 'Latencia',
      punto: 'Escritura en Blockchain no es instantánea. API es asíncrono',
      severidad: 'medium'
    },
    {
      categoria: 'Patrón Off-Chain/On-Chain',
      punto: 'Estándar de industria: datos pesados off-chain, pruebas de integridad on-chain',
      severidad: 'high'
    }
  ];

  const stackTecnologico = {
    frontend: ['React 18', 'Material-UI', 'Redux Toolkit'],
    backend: ['.NET Core 8', 'Entity Framework', 'ASP.NET Web API'],
    blockchain: ['Nethereum', 'Ethereum/Hyperledger', 'SHA-256'],
    storage: ['Azure Blob Storage', 'SQL Server', 'Azure Key Vault']
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 8 }}>
      <Container maxWidth="lg">
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/proyecto')}
          sx={{ mb: 4 }}
        >
          Volver a Presentación
        </Button>

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" fontWeight="bold" gutterBottom>
            Integración Blockchain
          </Typography>
          <Typography variant="h5" color="text.secondary" paragraph>
            Certificación de Integridad de Documentos Secretos
          </Typography>
          <Alert severity="info" sx={{ mt: 3, maxWidth: 800, mx: 'auto' }}>
            <strong>Concepto Clave:</strong> No se almacena el documento en blockchain, sino un registro inmutable de eventos (hashes) que certifica su integridad.
          </Alert>
        </Box>

        {/* Conceptos Fundamentales */}
        <Box sx={{ mb: 6 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom mb={3}>
                ¿Para qué usamos Blockchain?
            </Typography>
        
            {/* CSS Grid puro */}
            <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)'
                },
                gap: 3
            }}>
                {conceptosClave.map((concepto, index) => (
                <Card 
                    key={index}
                    sx={{ 
                    height: 240,
                    display: 'flex',
                    flexDirection: 'column',
                    textAlign: 'center'
                    }}
                >
                    <CardContent sx={{ 
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    p: 3
                    }}>
                    <Box sx={{ mb: 2 }}>
                        {concepto.icon}
                    </Box>
                    <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        gutterBottom
                    >
                        {concepto.titulo}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {concepto.descripcion}
                    </Typography>
                    </CardContent>
                </Card>
                ))}
            </Box>
        </Box>

        {/* Arquitectura Híbrida */}
        <Box sx={{ mb: 6 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom mb={3}>
                Arquitectura Híbrida Propuesta
            </Typography>
        
            {/* CSS Grid puro */}
            <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, 1fr)'
                },
                gap: 3
            }}>
                {arquitectura.map((item, index) => (
                <Card 
                    key={index}
                    sx={{ 
                    height: 200,
                    display: 'flex',
                    flexDirection: 'column'
                    }}
                >
                    <CardContent sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column'
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Chip 
                        label={item.tipo} 
                        color={item.color}
                        sx={{ fontWeight: 'bold' }}
                        />
                        <Typography variant="h6" fontWeight="bold">
                        {item.titulo}
                        </Typography>
                    </Box>
                    <Typography 
                        variant="body1" 
                        color="text.secondary"
                    >
                        {item.descripcion}
                    </Typography>
                    </CardContent>
                </Card>
                ))}
            </Box>
        </Box>

        {/* Diagramas de Flujo */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom mb={3}>
            Diagramas de Flujo
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant={selectedDiagram === 'radicacion' ? 'contained' : 'outlined'}
              onClick={() => setSelectedDiagram('radicacion')}
            >
              Flujo de Radicación
            </Button>
            <Button
              variant={selectedDiagram === 'verificacion' ? 'contained' : 'outlined'}
              onClick={() => setSelectedDiagram('verificacion')}
            >
              Flujo de Verificación
            </Button>
          </Box>

          <Paper sx={{ p: 4, bgcolor: 'white' }}>
  {selectedDiagram === 'radicacion' ? (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Flujo de Radicación de Documentos
      </Typography>
      <Box sx={{ my: 3, overflow: 'auto', textAlign: 'center' }}>
        <MermaidDiagram chart={`
graph TD
    A[Cliente envía Documento Secreto] --> B[Backend .NET Core 8]
    B --> C[Generar Hash SHA-256 del Documento]
    C --> D[Guardar Documento en Almacenamiento Seguro<br/>BD/Blob Storage Encriptado]
    D --> E[Firmar Transacción con Clave Privada]
    E --> F[Enviar Transacción a Blockchain<br/>Contiene Hash y Metadatos]
    F --> G[Recibir Hash de Transacción TxID]
    G --> H[Guardar TxID y Datos en BD Local<br/>para búsquedas rápidas]
    
    style A fill:#e3f2fd
    style B fill:#bbdefb
    style C fill:#90caf9
    style D fill:#64b5f6
    style E fill:#42a5f5
    style F fill:#2196f3
    style G fill:#1e88e5
    style H fill:#1976d2
        `} />
      </Box>
      <List>
        <ListItem>
          <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
          <ListItemText 
            primary="1. Cliente envía Documento Secreto" 
            secondary="El usuario carga el archivo a través del frontend"
          />
        </ListItem>
        {/* ... resto de ListItems igual ... */}
      </List>
    </Box>
  ) : (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Flujo de Verificación de Integridad
      </Typography>
      <Box sx={{ my: 3, overflow: 'auto', textAlign: 'center' }}>
        <MermaidDiagram chart={`
graph TD
    A[Solicitud de Verificación<br/>de Documento] --> B[Backend .NET Core 8]
    B --> C[Recuperar Documento<br/>del Almacenamiento Seguro]
    C --> D[Calcular Hash Nuevo<br/>del Documento]
    D --> E[Recuperar Hash Original<br/>desde Blockchain usando TxID]
    E --> F{¿Hashes coinciden?}
    F -->|Sí| G[INTEGRIDAD<br/>VERIFICADA]
    F -->|No| H[ALERTA:<br/>Documento Alterado]
    
    style A fill:#e8f5e9
    style B fill:#c8e6c9
    style C fill:#a5d6a7
    style D fill:#81c784
    style E fill:#66bb6a
    style F fill:#ffd54f
    style G fill:#4caf50
    style H fill:#f44336
        `} />
      </Box>
      <List>
        <ListItem>
          <ListItemIcon><Storage color="secondary" /></ListItemIcon>
          <ListItemText 
            primary="1. Recuperar Documento" 
            secondary="Se obtiene el archivo del almacenamiento seguro"
          />
        </ListItem>
        {/* ... resto de ListItems igual ... */}
      </List>
    </Box>
  )}
</Paper>
        </Box>

        {/* Stack Tecnológico */}
        <Box sx={{ mb: 6 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom mb={3}>
                Stack Tecnológico Blockchain
            </Typography>
        
            {/* CSS Grid puro */}
            <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)'
                },
                gap: 2
            }}>
                {Object.entries(stackTecnologico).map(([categoria, tecnologias], index) => (
                <Card 
                    key={index}
                    sx={{ 
                    height: 220,
                    display: 'flex',
                    flexDirection: 'column'
                    }}
                >
                    <CardContent sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column'
                    }}>
                    <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        gutterBottom 
                        textTransform="capitalize"
                    >
                        {categoria}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ 
                        flexGrow: 1,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        alignContent: 'flex-start'
                    }}>
                        {tecnologias.map((tech, idx) => (
                        <Chip 
                            key={idx}
                            label={tech}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                        ))}
                    </Box>
                    </CardContent>
                </Card>
                ))}
            </Box>
        </Box>

        {/* Consideraciones de Seguridad */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom mb={3}>
            Consideraciones Críticas de Seguridad
          </Typography>
          {consideracionesSeguridad.map((item, index) => (
            <Alert 
              key={index}
              severity={item.severidad === 'critical' ? 'error' : item.severidad === 'high' ? 'warning' : 'info'}
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                {item.categoria}
              </Typography>
              <Typography variant="body2">
                {item.punto}
              </Typography>
            </Alert>
          ))}
        </Box>

        {/* Implementación .NET Core */}
        <Card sx={{ mb: 6, bgcolor: 'grey.900', color: 'white' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Implementación en .NET Core 8
            </Typography>
            <Typography variant="body1" paragraph>
              La integración utiliza <strong>Nethereum</strong> como cliente .NET para interactuar con Ethereum/Hyperledger.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Servicios Principales
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="• IBlockchainService" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="• DocumentService" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="• IStorageService" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Modelos
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="• DocumentRecord" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="• RadicationResult" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="• TransactionHash" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Controllers
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="• DocumentsController" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="• [HttpPost] RadicateDocument" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="• [HttpGet] VerifyDocument" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Conclusión */}
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Conclusión
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            La integración de Blockchain en Tracker ON es técnicamente correcta y agrega un nivel de confianza 
            extremadamente alto en la inmutabilidad del registro. La clave está en que usamos Blockchain para lo que 
            es bueno (consenso e inmutabilidad) y sistemas tradicionales seguros para almacenamiento eficiente.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Chip icon={<CheckCircle />} label="Integridad Garantizada" color="success" />
            <Chip icon={<Timeline />} label="Trazabilidad Completa" color="primary" />
            <Chip icon={<Security />} label="Seguridad Avanzada" color="secondary" />
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default BlockchainIntegration;